import { Column } from 'primereact/column'
import { DataTable, DataTableExpandedRows } from 'primereact/datatable'
import { FC, useState } from 'react'
import { RunStatus, WorkflowTask } from '../../types/types'
import { CloseIcon, CollapseIcon, ExpandIcon, RedoIcon, TimerOffIcon } from '../Icons'
import { CompactTableHeaderClassName, WorkflowStatusDef } from '../../utils/constants'
import { classNames } from 'primereact/utils'
import { RunStatusCircle } from './StatusCircle'
import IconButton from '../buttons/IconButton'
import { useRecommitRun, useCancelTask, useTimeoutTask } from '../../api/submission-API'
import { toast } from 'react-hot-toast'
import { confirmDialog } from 'primereact/confirmdialog'
import { useRecoilValue } from 'recoil'
import { currentUserState } from '../../utils/state-atoms'
import { canMutate } from '../../utils/roles'

type TaskListProp = {
  runId: string
  workflowRunGroupSysId: string
  workflowSystemRunId: string
  tasks: any[]
  // Superseded (Prior) cases: blocks resubmit only — cancel/timeout stay available so a job
  // started on a retired case can still be stopped.
  disabled?: boolean
}

const TaskList: FC<TaskListProp> = ({
  runId,
  workflowRunGroupSysId,
  workflowSystemRunId,
  tasks,
  disabled
}: TaskListProp) => {
  const [{ loading: submittingTask }, resubmitTask] = useRecommitRun()
  const [{ loading: cancellingTask }, cancelTask] = useCancelTask()
  const [{ loading: timingOutTask }, timeoutTask] = useTimeoutTask()
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({})
  const currentUser = useRecoilValue(currentUserState)
  const userCanMutate = canMutate(currentUser?.role?.roleId)

  const handleResubmit = async (taskId: string, status: RunStatus) => {
    if (submittingTask) {
      return
    }

    const verb = status === RunStatus.CREATED ? 'submit' : 'resubmit'

    confirmDialog({
      message: `Are you sure you want to ${verb} this task?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'reject',
      rejectClassName: 'bg-gray-500 text-white',
      accept: async () => {
        try {
          await resubmitTask({
            runId,
            workflowRunGroupSysId,
            workflowSystemRunId,
            taskId
          } as any)
          toast.success(`Task ${verb}ted.`)
        } catch (error) {
          toast.error(`Failed to ${verb} task.`)
          console.log(error)
        }
      }
    })
  }

  const handleCancelTask = async (taskId: string) => {
    if (submittingTask) {
      return
    }
    try {
      await cancelTask({
        runId,
        runGroupId: workflowRunGroupSysId,
        workflowId: workflowSystemRunId,
        taskId
      } as any)
      toast.success('Task cancelled.')
    } catch (error) {
      toast.error('Failed to cancel task.')
      console.log(error)
    }
  }

  const handleTimeout = async (gmRefString: string) => {
    if (timingOutTask) {
      return
    }
    try {
      await timeoutTask({
        gmRefString
      } as any)
      toast.success('Task timed out.')
    } catch (error) {
      toast.error('Failed to timeout task.')
      console.log(error)
    }
  }

  const columns = [
    {
      header: 'Task ID',
      field: '_id',
      headerClassName: classNames(CompactTableHeaderClassName, 'w-[150px]'),
      body: (rowData: WorkflowTask) => {
        const rowName: string = rowData.systemRunId

        return (
          <div
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => {
              // get current row id => if in expandRows, remove; else, append
              const copyExpandedRows: DataTableExpandedRows = { ...expandedRows }
              if (expandedRows[rowName]) {
                // if rowName exists in expandedRows
                // delete rowName from expandedRows
                delete copyExpandedRows[rowName]
              } else {
                // if rowName doesnt exist, add rowName to expandedRows
                copyExpandedRows[rowName] = true
              }
              setExpandedRows(copyExpandedRows)
            }}>
            {expandedRows[rowName] ? (
              <CollapseIcon className="fill-dark-blue dark:fill-light-blue h-6 w-6" />
            ) : (
              <ExpandIcon className="fill-dark-blue dark:fill-light-blue h-6 w-6" />
            )}
            <span className="text-gm-blue dark:text-light-blue">{rowData.systemRunId}</span>
          </div>
        )
      }
    },
    {
      header: 'Name',
      field: 'name',
      headerClassName: CompactTableHeaderClassName,
      body: (rowData: WorkflowTask) => {
        if (rowData.name) {
          return rowData.name
        } else if (rowData.platformJob) {
          return rowData.platformJob.name
        } else if (rowData.rescaleJob) {
          return rowData.rescaleJob.operation
        } else {
          return ''
        }
      }
    },
    {
      header: 'Description',
      field: 'description',
      headerClassName: CompactTableHeaderClassName,
      body: (rowData: WorkflowTask) => {
        if (rowData.platformJob) {
          return rowData.platformJob.description
        } else {
          return rowData.rescaleJob && rowData.rescaleJob.name
        }
      }
    },
    {
      header: 'Type',
      // field: 'platformJob.type',
      headerClassName: classNames(CompactTableHeaderClassName, 'w-24'),
      body: (rowData: WorkflowTask) => {
        if (rowData.platformJob) {
          return 'Platform'
        } else if (rowData.rescaleJob) {
          return 'Rescale'
        } else {
          return ''
        }
      }
    },
    {
      header: 'Status',
      field: 'status',
      headerClassName: classNames(CompactTableHeaderClassName, 'w-[180px]'),
      body: (rowData: WorkflowTask) => {
        const status: RunStatus = rowData.status?.runStatus
        return (
          <div className="flex items-center space-x-1">
            <RunStatusCircle status={status} />
            <span className="mr-4 flex items-center align-middle">
              {WorkflowStatusDef[status]?.name}
            </span>
            {!disabled &&
              userCanMutate &&
              (status === RunStatus.ERROR ||
                status === RunStatus.WARN_ON_ERROR ||
                status === RunStatus.COMPLETED ||
                status === RunStatus.CREATED) && (
                <IconButton
                  title={status === RunStatus.CREATED ? 'Submit' : 'Resubmit'}
                  severity="danger"
                  onClick={() => handleResubmit(rowData.systemRunId, status)}
                  loading={submittingTask}
                  icon={<RedoIcon className="fill-white w-4 h-4" />}
                />
              )}
            {userCanMutate && status !== RunStatus.COMPLETED && status !== RunStatus.RUNNING && (
              <IconButton
                title="Cancel"
                severity="danger"
                onClick={() => handleCancelTask(rowData.systemRunId)}
                loading={cancellingTask}
                icon={<CloseIcon className="fill-white w-4 h-4" />}
              />
            )}
            {userCanMutate && status === RunStatus.RUNNING && (
              <IconButton
                title="Timeout Task"
                severity="danger"
                onClick={() => {
                  confirmDialog({
                    message: `Are you sure you want to timeout this task? This action is meant for hung tasks in the RUNNING state.`,
                    header: 'Confirmation',
                    icon: 'pi pi-exclamation-triangle',
                    defaultFocus: 'reject',
                    rejectClassName: 'bg-gray-500 text-white',
                    accept: () => {
                      handleTimeout(rowData.gmRefString)
                    }
                  })
                }}
                loading={timingOutTask}
                icon={<TimerOffIcon className="fill-white w-4 h-4" />}
              />
            )}
          </div>
        )
      }
    },
    {
      header: 'Active',
      field: 'active',
      headerClassName: classNames(CompactTableHeaderClassName, 'w-20'),
      body: (rowData: WorkflowTask) => {
        if (rowData.platformJob) {
          return rowData.platformJob.active ? 'Yes' : 'No'
        } else {
          return ''
        }
      }
    },
    {
      header: 'Time',
      field: 'active',
      headerClassName: classNames(CompactTableHeaderClassName, 'w-[150px]'),
      body: (rowData: WorkflowTask) => {
        if (
          rowData.status?.startTime &&
          rowData.status?.submitTime &&
          rowData.status?.completeTime
        ) {
          const startTime = new Date(rowData.status.startTime)
          const submitTime = new Date(rowData.status.submitTime)
          const completeTime = new Date(rowData.status.completeTime)
          const runTime = completeTime.getTime() - startTime.getTime()
          const queueTime = startTime.getTime() - submitTime.getTime()
          const totalTime = runTime + queueTime
          const runTimeMins = runTime / 1000 / 60
          const queueTimeMins = queueTime / 1000 / 60
          const totalTimeMins = totalTime / 1000 / 60
          const runTimeWidth = Math.round((runTime / totalTime) * 120)
          const queueTimeWidth = Math.round((queueTime / totalTime) * 120)
          let queueTimeTitle
          if (queueTime === 0) {
            queueTimeTitle = `Queue time: 0 sec`
          } else if (queueTime < 60 * 1000) {
            queueTimeTitle = `Queue time: ${(queueTime / 1000).toFixed(3)} secs`
          } else {
            queueTimeTitle = `${queueTimeMins.toFixed(3)} mins`
          }

          let runTimeTitle = ''
          if (runTimeMins === 0) {
            runTimeTitle = `Run time: 0 sec`
          } else if (runTime < 60 * 1000) {
            runTimeTitle = `Run time: ${(runTime / 1000).toFixed(3)} secs`
          } else {
            runTimeTitle = `${runTimeMins.toFixed(3)} mins`
          }

          let totalTimeTitle = ''
          if (totalTime === 0) {
            totalTimeTitle = 'Total: 0 sec'
          } else if (totalTime < 60 * 1000) {
            // avoid displaying "Total: NaN secs" when totalTime is NaN
            totalTimeTitle = Number.isNaN(totalTime)
              ? ''
              : `Total: ${(totalTime / 1000).toFixed(3)} secs`
          } else {
            // avoid displaying "Total: NaN mins" when totalTime is NaN
            totalTimeTitle = Number.isNaN(totalTimeMins)
              ? ''
              : `Total: ${totalTimeMins.toFixed(3)} mins`
          }

          return (
            <>
              <div className="text-xs">{totalTimeTitle}</div>
              <div className="flex flex-row items-stretch h-3">
                <div
                  className={`bg-gm-aqua-dark w-[${queueTimeWidth}px]`}
                  title={queueTimeTitle}></div>
                <div className={`bg-gm-blue w-[${runTimeWidth}px]`} title={runTimeTitle}></div>
              </div>
            </>
          )
        } else {
          return <div>N/A</div>
        }
      }
    }
  ]

  return (
    <DataTable
      value={tasks}
      dataKey="systemRunId"
      pt={{
        root: {
          className: 'mb-4'
        },
        bodyRow: {
          className: 'table-row'
        }
      }}
      expandedRows={expandedRows}
      rowExpansionTemplate={(rowData: WorkflowTask) => {
        let platformJob
        let rescaleJob
        let gmRefString = rowData?.gmRefString || ''
        let warnOnException = rowData?.warnOnException

        if (rowData.platformJob && rowData.status) {
          platformJob = {
            ...rowData.platformJob,
            status: rowData.status
          }
        }
        if (rowData.rescaleJob && rowData.status) {
          rescaleJob = {
            ...rowData.rescaleJob,
            status: rowData.status
          }
        }

        return (
          <>
            {gmRefString && (
              <div className="pl-8 max-w-[100%]">
                <pre className="whitespace-pre-wrap">{JSON.stringify(gmRefString, null, 2)}</pre>
              </div>
            )}
            {warnOnException && (
              <div className="pl-8 max-w-[100%]">
                <pre className="whitespace-pre-wrap">
                  {JSON.stringify(warnOnException, null, 2)}
                </pre>
              </div>
            )}
            {platformJob && (
              <div className="pl-8 max-w-[100%]">
                <pre className="whitespace-pre-wrap">{JSON.stringify(platformJob, null, 2)}</pre>
              </div>
            )}
            {rescaleJob && (
              <div className="pl-8 max-w-[100%]">
                <pre className="whitespace-pre-wrap">{JSON.stringify(rescaleJob, null, 2)}</pre>
              </div>
            )}
          </>
        )
      }}>
      {columns.map(item => {
        return (
          <Column
            key={item.header}
            field={item.field}
            header={item.header}
            headerClassName={item.headerClassName}
            body={item.body}
          />
        )
      })}
    </DataTable>
  )
}

export default TaskList
